import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { fromEvent, merge } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../core/reducers';
import {
  LayoutUtilsService,
  MessageType,
  QueryParamsModel,
} from '../../../../../../core/_base/crud';
import {
  Insurance,
  InsurancesDataSource,
  InsurancesPageRequested,
  selectErrorState,
  selectInsurancesActionLoading,
  ViewContext,
} from '../../../../../../core/lists';
import { TranslateService } from '@ngx-translate/core';
import { InsuranceOnServerDeleted } from 'src/app/core/lists/_actions/insurance.actions';
import { StringUtilsService } from '../../../../../../core/services/string-utils.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NavigationService } from '../../../../../../core/services/navigation.service';
import { NgxPermissionsService } from 'ngx-permissions';

@Component({
  selector: 'kt-insurances-list',
  templateUrl: './insurances-list.component.html',
  styleUrls: ['./insurances-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsurancesListComponent implements OnInit, OnDestroy {
  dataSource: InsurancesDataSource;
  displayedColumns = [
    'id',
    'name',
    'insuranceType',
    'submissionType',
    'bin',
    'pcn',
    'phone',
    'actions',
  ];
  searchBarColumns = [
    'searchID',
    'searchName',
    'searchInsuranceType',
    'searchSubmissionType',
    'searchBin',
    'searchPcn',
    'searchPhone',
    'searchActions',
  ];
  showSearchBar = false;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort', { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  searchIDInput = '';
  searchNameInput = '';
  searchInsuranceTypeInput = '';
  searchSubmissionTypeInput = '';
  searchBinInput = '';
  searchPcnInput = '';
  searchPhoneInput = '';
  statusTracker = 0;
  submissionErrorObj: any;
  hasSubmissionErrors: boolean;
  private subscriptions: any[] = [];
  searchForm: FormGroup;
  filtersApplied: any = false;
  firstLoadAfterBack: any = false;
  pageSize: any;

  constructor(
    private navigationService: NavigationService,
    private permissionsService: NgxPermissionsService,
    private fb: FormBuilder,
    public dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private layoutUtilsService: LayoutUtilsService,
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    public stringUtils: StringUtilsService,
  ) {}

  ngOnInit() {
    // Comment by Nourhan: please don't change the order of this line
    this.filtersApplied = this.navigationService.getFilters();

    const sortSubscription = this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = this.paginator?.pageIndex || 0;
      this.sort.active = this.sort.direction ? this.sort.active : 'created';
      this.sort.direction = this.sort.direction || 'desc';
    });
    this.subscriptions.push({ id: 1, sub: sortSubscription });

    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        tap(() => {
          this.loadInsurancesList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 2, sub: paginatorSubscriptions });

    const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.loadInsurancesList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 3, sub: searchSubscription });

    this.dataSource = new InsurancesDataSource(this.store);

    this.searchForm = this.fb.group({
      search: [],
    });

    // Comment by Nourhan: please don't change the order of this scope
    if (this.filtersApplied) {
      this.firstLoadAfterBack = true;
      this.pageSize = this.filtersApplied.pageSize;
    }

    const routeSubscription = this.activatedRoute.queryParams.subscribe(() => {
      this.loadInsurancesList();
    });
    this.subscriptions.push({ id: 5, sub: routeSubscription });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  loadInsurancesList() {
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'desc',
      this.sort.active || 'created',
      this.paginator.pageIndex,
      this.paginator.pageSize || this.pageSize || 25,
    );
    this.store.dispatch(new InsurancesPageRequested({ page: queryParams }));
  }

  filterConfiguration(reset_applied_flag?: any): any {
    const filter: any = {};
    if (this.filtersApplied && this.firstLoadAfterBack) {
      this.firstLoadAfterBack = false;
      const appliedFilters = this.filtersApplied;
      this.showSearchBar = appliedFilters.showSearchBar;
      if (this.showSearchBar) {
        this.searchIDInput = appliedFilters.id;
        this.searchNameInput = appliedFilters.name;
        this.searchInsuranceTypeInput = appliedFilters.insuranceType;
        this.searchSubmissionTypeInput = appliedFilters.submissionType;
        this.searchBinInput = appliedFilters.bin;
        this.searchPcnInput = appliedFilters.pcn;
        this.searchPhoneInput = appliedFilters.phone;
      } else {
        if (this.searchInput?.nativeElement) {
          this.searchInput.nativeElement.value = appliedFilters.searchTerm || '';
        }
      }
      this.sort.direction = this.filtersApplied?.sortDirection || 'desc';
      this.sort.active = this.filtersApplied?.sortActive || 'created';
      this.paginator.pageIndex = this.filtersApplied?.pageIndex || 0;
      this.paginator.pageSize = this.filtersApplied?.pageSize || 25;
    }
    if (reset_applied_flag) this.filtersApplied = false;
    if (this.showSearchBar) {
      filter.id = this.searchIDInput;
      filter.name = this.searchNameInput;
      filter.insuranceType = this.searchInsuranceTypeInput;
      filter.submissionType = this.searchSubmissionTypeInput;
      filter.bin = this.searchBinInput;
      filter.pcn = this.searchPcnInput;
      filter.phone = this.searchPhoneInput?.replace(/[()\-\s]/g, '');
      filter.showSearchBar = true;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
      filter.showSearchBar = false;
    }
    filter.sortDirection = this.sort.direction || 'desc';
    filter.sortActive = this.sort.active || 'created';
    filter.pageIndex = this.paginator.pageIndex;
    filter.pageSize = this.paginator.pageSize;
    return filter;
  }

  deleteInsurance(_item: Insurance) {
    const _title: string = this.translate.instant('INSURANCE.DELETE_INSURANCE_SIMPLE.TITLE');
    const _description: string = this.translate.instant(
      'INSURANCE.DELETE_INSURANCE_SIMPLE.DESCRIPTION',
    );
    const _waitDescription: string = this.translate.instant(
      'INSURANCE.DELETE_INSURANCE_SIMPLE.WAIT_DESCRIPTION',
    );
    const _deleteMessage = this.translate.instant('INSURANCE.DELETE_INSURANCE_SIMPLE.MESSAGE');
    const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDescription);
    const dialogRefSubscription = dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      this.store.dispatch(new InsuranceOnServerDeleted({ id: _item.id }));
      const actionLoadingSubscription = this.store
        .pipe(select(selectInsurancesActionLoading))
        .subscribe((response) => {
          if (response !== true) {
            const errorStateSubscription = this.store
              .pipe(select(selectErrorState))
              .subscribe((error) => {
                if (error) {
                  if (error.error && error.error.errors) {
                    this.submissionErrorObj = error.error.errors;
                  } else {
                    this.submissionErrorObj = [{ error: 'GLOBAL.UNSPECIFIED_ERROR' }];
                  }
                  this.hasSubmissionErrors = true;
                } else {
                  this.submissionErrorObj = null;
                  this.hasSubmissionErrors = false;
                }
              });
            if (this.subscriptions.find((sub) => sub.id === 9) === undefined) {
              this.subscriptions.push({ id: 9, sub: errorStateSubscription });
            }
            if (this.submissionErrorObj !== null) {
              const errArray = [];
              this.submissionErrorObj.forEach((errObj: { error: string | string[] }) => {
                const message = this.translate.instant(errObj.error);
                errArray.push(message);
              });
              const errMsg = errArray.join('\n');
              this.layoutUtilsService.showActionNotification(
                errMsg,
                MessageType.Delete,
                3000,
                true,
                false,
              );
            } else {
              this.layoutUtilsService.showActionNotification(
                _deleteMessage,
                MessageType.Delete,
                3000,
                true,
                false,
              );
              this.loadInsurancesList();
            }
          }
          this.cdr.detectChanges();
        });
      if (this.subscriptions.find((sub) => sub.id === 8) === undefined) {
        this.subscriptions.push({ id: 8, sub: actionLoadingSubscription });
      }
    });
    if (this.subscriptions.find((sub) => sub.id === 7) === undefined) {
      this.subscriptions.push({ id: 7, sub: dialogRefSubscription });
    }
  }

  canEditInsurance(): boolean {
    return !!this.permissionsService.getPermission('canEditInsurance');
  }

  createInsurance() {
    this.navigationService.navigateTo('lists/insurances/add');
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  editInsurance(id: number) {
    const queryParams = {
      viewContext: ViewContext.insurances,
    };

    this.navigationService.navigateTo('lists/insurances/edit/' + id, queryParams);
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  reloadInsurancesList(): void {
    this.paginator.pageIndex = 0;
    this.loadInsurancesList();
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();
    this.searchNameInput = '';
    this.searchIDInput = '';
    this.searchInsuranceTypeInput = '';
    this.searchSubmissionTypeInput = '';
    this.searchBinInput = '';
    this.searchPcnInput = '';
    this.searchPhoneInput = '';
  }

  resetSearchInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadInsurancesList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
    } else {
      this.resetSearchBar();
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadInsurancesList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();
    this.reloadInsurancesList();
  }
}

